import { IInformationMessage } from "@faire/web-api/indigofair/data/IInformationMessage";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { FaireDateRange } from "@faire/web/common/FaireDateRange";
import { useStrictLocalization } from "@faire/web/common/localization";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Button } from "@faire/web/slate/Button";
import { Calendar } from "@faire/web/slate/icons/foundation/Calendar";
import { Information } from "@faire/web/slate/icons/foundation/Information";
import { Shipping } from "@faire/web/slate/icons/foundation/Shipping";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import * as React from "react";
import styled, { css } from "styled-components";

import { TextWithTooltip } from "@faire/retailer/components/TextWithTooltip";
import { ExclusivityDetailsLink } from "@faire/retailer/features/Exclusivity/DetailsModal/ExclusivityDetailsLink";
import { useExclusivityAvailabilityWithState } from "@faire/retailer/hooks/exclusivity/useExclusivityAvailabilityWithState";
import { isLoggedInRetailerOrBrandPreview } from "@faire/retailer/lib/isLoggedInRetailer";
import { getSettingInternationalBuyingNudges } from "@faire/retailer/settings/getSettingInternationalBuyingNudges";
import { getSettingOwFImproveTermsTouchpointsPDP } from "@faire/retailer/settings/getSettingOwFImproveTermsTouchpointsPDP";
import { buildFulfillmentTime } from "@faire/retailer/views/brand/utils/leadTimeUtils";

import { ShippingEstimate } from "./ShippingEstimate";

type ShippingDetailsItem = {
  icon?: React.ReactNode;
  text: React.ReactNode;
  id: string;
};

export const ShippingDetailsList: React.FC<{
  brand: IProductPageResponse.IPdpBrand;
  infoTexts?: IInformationMessage[];
  importShipmentInfo?: string;
  preorderDateRange?: FaireDateRange;
  shippingPromotion?: IInformationMessage;
}> = ({
  brand,
  infoTexts,
  importShipmentInfo,
  preorderDateRange,
  shippingPromotion,
}) => {
  const shipsFrom: Array<ShippingDetailsItem> = [];
  const { strictLocalize } = useStrictLocalization();

  const brandHasNecessaryShippingDetails =
    isNotUndefined(brand.lower_bound_lead_time_days) &&
    isNotUndefined(brand.upper_bound_lead_time_days) &&
    isNotUndefined(brand.shipping_time_lower_bound_days) &&
    isNotUndefined(brand.shipping_time_upper_bound_days);

  if (brandHasNecessaryShippingDetails) {
    shipsFrom.push({
      icon: <Calendar />,
      text: (
        <ShippingEstimate brand={brand} preorderDateRange={preorderDateRange} />
      ),
      id: "SHIPPING_ESTIMATE_CUSTOM",
    });
  }
  const hasLeadTimeAsRange =
    brand.upper_bound_lead_time_days !== brand.lower_bound_lead_time_days &&
    isNotUndefined(brand.lower_bound_lead_time_days) &&
    isNotUndefined(brand.upper_bound_lead_time_days);
  if (!isLoggedInRetailerOrBrandPreview() && hasLeadTimeAsRange) {
    shipsFrom.push({
      id: "FULFILLMENT_TIME_CUSTOM",
      icon: <Calendar />,
      text: (
        <Typography>
          {buildFulfillmentTime(
            {
              leadTimeDays: brand.lead_time_days,
              upperBoundLeadTimeDays: brand.upper_bound_lead_time_days,
              lowerBoundLeadTimeDays: brand.lower_bound_lead_time_days,
            },
            strictLocalize
          )}
        </Typography>
      ),
    });
  }

  if (infoTexts) {
    infoTexts.map((infoText, index) => {
      shipsFrom.push({
        id: infoText.type ?? `UNKNOWN_${index}`,
        icon: infoText.icon_url ? (
          <IconImg
            src={infoText.icon_url}
            $iconSize={getIconImgSize(infoText.type)}
          />
        ) : null,
        text: (
          <Typography>
            {infoText.text}{" "}
            <CustomLink
              linkUrl={infoText.help_link}
              brandToken={brand.token}
              type={infoText.type}
              ctaText={infoText.cta_text}
            />
          </Typography>
        ),
      });
    });
  } else if (importShipmentInfo && getSettingInternationalBuyingNudges()) {
    shipsFrom.push({
      id: "IMPORT_SHIPMENT_INFO",
      icon: null,
      text: <Typography>{importShipmentInfo}</Typography>,
    });
  }

  if (
    shippingPromotion?.type &&
    shippingPromotion?.text &&
    shippingPromotion?.tooltip
  ) {
    shipsFrom.push({
      id: shippingPromotion.type,
      icon: <Shipping width="21px" height="21px" />,
      text: (
        <TextWithTooltip
          text={shippingPromotion.text}
          tooltipText={shippingPromotion.tooltip}
        />
      ),
    });
  }

  if (shipsFrom.length <= 0) {
    return null;
  }

  if (getSettingOwFImproveTermsTouchpointsPDP()) {
    return (
      <>
        <Spacer size="3x" />
        <Column>
          <Typography variant="paragraphSansMedium">
            <StrictLocalMsg
              defaultMessage="Shipping & policies"
              description={{
                text: "Title text above some shipping and other policy information for a product",
                img: "https://cdn.faire.com/fastly/5fbd830f06e5b9b3c7f2d0655df159adf1f6e7f3a623979b956e18034f696df7.png",
              }}
            />
          </Typography>
          <Spacer size={12} />
          <ul>
            {shipsFrom.map((item) => (
              <ListItemNoBullet key={`shipping-info-${item.id}`}>
                <Row gap="1x">
                  <IconWrapper>{item.icon ?? <Information />}</IconWrapper>
                  {item.text}
                </Row>
              </ListItemNoBullet>
            ))}
          </ul>
        </Column>
      </>
    );
  }

  return (
    <>
      <Spacer size="3x" />
      <Column>
        <Typography variant="paragraphSansMedium">
          <StrictLocalMsg
            defaultMessage="Shipping"
            description={{
              text: "Refers to how the order will be delivered to the retailer",
            }}
          />
        </Typography>
        <Spacer size="1x" />
        <ul>
          {shipsFrom.map((item) => (
            <ListItem key={`shipping-info-${item.id}`}>{item.text}</ListItem>
          ))}
        </ul>
      </Column>
    </>
  );
};

const CustomLink: React.FC<{
  linkUrl: string | undefined;
  ctaText: string | undefined;
  brandToken: string | undefined;
  type: keyof typeof IInformationMessage.Type | undefined;
}> = ({ linkUrl, type, ctaText, brandToken }) => {
  const { availability } = useExclusivityAvailabilityWithState(brandToken);

  if (!getSettingOwFImproveTermsTouchpointsPDP() || !ctaText) {
    return null;
  }

  if (
    type === IInformationMessage.Type.EXCLUSIVITY_ACTIVE &&
    availability?.exclusivity_agreement_token
  ) {
    return (
      <ExclusivityDetailsLink
        agreementToken={availability?.exclusivity_agreement_token}
        brandToken={brandToken}
        origin="product"
        color="inherit"
      >
        {ctaText}
      </ExclusivityDetailsLink>
    );
  }

  if (type === IInformationMessage.Type.EXCLUSIVITY_AVAILABLE && linkUrl) {
    return (
      <Button variant="tertiary" linkTo={linkUrl} tertiaryColor="inherit">
        {ctaText}
      </Button>
    );
  }

  return null;
};

const getIconImgSize = (
  messageType: keyof typeof IInformationMessage.Type | undefined
): number => {
  switch (messageType) {
    case IInformationMessage.Type.EXCLUSIVITY_ACTIVE:
    case IInformationMessage.Type.EXCLUSIVITY_AVAILABLE:
      return 22;
    case IInformationMessage.Type.SHIPS_FROM:
      return 18;
    default:
      return 20;
  }
};

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  width: 20px;
  flex-shrink: 0;
`;

const IconImg = styled.img<{ $iconSize: number }>`
  ${({ $iconSize }) => css`
    height: ${$iconSize}px;
    width: ${$iconSize}px;
  `};
`;

const ListItem = styled.li`
  list-style-type: disc;
  margin-left: ${getSpacing("2x")};
`;

const ListItemNoBullet = styled.li`
  list-style-type: none;
  padding: 0;
  padding-bottom: 12px;
  margin: 0;
`;
